import React, { useState, useContext } from 'react';
import { api } from '../../services/baseUrl';
import { ThemeContext } from '../../hooks/ThemeContext';
import { useNavigate } from 'react-router-dom';

const Register = () => {
  const [formData, setFormData] = useState({
    nickname: '',
    firstname: '',
    lastname: '',
    email: '',
    password: '',
    confirmPassword: '',
  });

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();
  const { theme } = useContext(ThemeContext);
  const isDark = theme === 'dark';

  const g = 'linear-gradient(135deg, #f43f5e 0%, #a855f7 50%, #6366f1 100%)';

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (formData.password !== formData.confirmPassword) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }

    try {
      await api.post('/register', {
        nickname: formData.nickname,
        firstname: formData.firstname,
        lastname: formData.lastname,
        email: formData.email,
        password: formData.password,
        password_confirmation: formData.confirmPassword,
        role: 'user',
      });
      setSuccess('Inscription réussie ! Redirection...');
      setTimeout(() => navigate('/login'), 1000);
    } catch (err) {
      setError(err.response?.data?.message || "Erreur lors de l'inscription. Vérifiez vos informations.");
    }
  };

  const inputClass = `w-full px-4 py-2.5 border rounded-xl shadow-sm transition focus:outline-none focus:ring-2 focus:ring-rose-500 ${
    isDark
      ? 'bg-white/[0.05] border-white/[0.08] text-white placeholder-gray-500'
      : 'bg-gray-50 border-gray-300 text-gray-800'
  }`;

  return (
    <div
      className="relative flex items-center justify-center min-h-screen px-4 py-12 overflow-hidden"
      style={{ backgroundColor: isDark ? '#07070a' : '#f9fafb' }}
    >
      {isDark && (
        <>
          <div
            className="absolute top-[-10%] right-[-5%] w-[400px] h-[400px] rounded-full pointer-events-none"
            style={{ background: 'radial-gradient(circle, #a855f733 0%, transparent 70%)', filter: 'blur(80px)' }}
          />
          <div
            className="absolute bottom-[-10%] left-[-5%] w-[360px] h-[360px] rounded-full pointer-events-none"
            style={{ background: 'radial-gradient(circle, #f43f5e33 0%, transparent 70%)', filter: 'blur(80px)' }}
          />
        </>
      )}

      <div
        className={`relative z-10 w-full max-w-md rounded-2xl p-8 border shadow-2xl ${
          isDark
            ? 'bg-white/[0.04] border-white/[0.08] text-white'
            : 'bg-white border-gray-200 text-gray-800 shadow-gray-200/60'
        }`}
      >
        <div className="flex justify-center mb-5">
          <span
            className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold border ${
              isDark ? 'bg-white/[0.06] border-white/[0.10] text-gray-300' : 'bg-rose-50 border-rose-200 text-rose-600'
            }`}
          >
            Créez votre compte
          </span>
        </div>

        <h1 className="text-3xl font-bold text-center mb-1">
          {isDark ? (
            <>
              Rejoignez{' '}
              <span style={{ backgroundImage: g, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                Krayne Agency.
              </span>
            </>
          ) : (
            'Rejoignez Krayne Agency.'
          )}
        </h1>
        <p className={`text-center text-sm mb-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          Suivez vos projets, commandes et messages depuis votre espace client.
        </p>

        {success && (
          <div className="mb-4 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm text-center">
            {success}
          </div>
        )}
        {error && (
          <div className="mb-4 p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-400 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Prénom</label>
              <input type="text" name="firstname" placeholder="Jean" value={formData.firstname} onChange={handleChange} required className={inputClass} />
            </div>
            <div>
              <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Nom</label>
              <input type="text" name="lastname" placeholder="Dupont" value={formData.lastname} onChange={handleChange} required className={inputClass} />
            </div>
          </div>

          <div>
            <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Pseudo</label>
            <input type="text" name="nickname" placeholder="pseudo123" value={formData.nickname} onChange={handleChange} required className={inputClass} />
          </div>

          <div>
            <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Email</label>
            <input type="email" name="email" placeholder="votre@email.com" value={formData.email} onChange={handleChange} required className={inputClass} />
          </div>

          <div>
            <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Mot de passe</label>
            <input type="password" name="password" placeholder="Min. 8 caractères" value={formData.password} onChange={handleChange} required minLength={8} className={inputClass} />
          </div>

          <div>
            <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Confirmer le mot de passe</label>
            <input type="password" name="confirmPassword" placeholder="••••••••" value={formData.confirmPassword} onChange={handleChange} required className={inputClass} />
          </div>

          <button
            type="submit"
            className="w-full py-3 px-4 text-white font-semibold rounded-xl shadow-lg transition duration-200"
            style={{ background: g }}
          >
            Créer mon compte
          </button>

          <button
            type="button"
            onClick={() => navigate('/login')}
            className={`w-full py-2.5 px-4 font-medium rounded-xl border transition duration-200 text-sm ${
              isDark
                ? 'border-white/[0.10] text-gray-300 hover:bg-white/[0.04]'
                : 'border-gray-200 text-gray-600 hover:bg-gray-50'
            }`}
          >
            Déjà un compte ?{' '}
            <span className="font-semibold" style={{ backgroundImage: g, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
              Se connecter
            </span>
          </button>
        </form>
      </div>
    </div>
  );
};

export default Register;
